// Pathing: src/components/student/AcievementsCard.jsx
// Focus: Achievements Component for tracking and associated functions. 
// Version Update: Refactored to use centralized stylesBranding utility, removed duplicated theme logic

import React, { useState, useEffect } from 'react';
import { Trophy } from 'lucide-react';
import { getProgressForBadge, isAchievementUnlocked } from '../../utils/trackingProgress';
import { getThemeColors } from '../../utils/stylesBranding';
import styles from './AchievementsCard.module.css';

const AchievementsCard = ({ achievements = [], subject = 'all' }) => {
  const themeColors = getThemeColors(subject);
  const [selected, setSelected] = useState(null);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [allAchievements, setAllAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use effect, for pulling our achievements.json via file path public /assets/achievements.jsons, need to add fall back logic for our cloud offering.
  useEffect(() => {
    const fetchAchievements = async () => {
      try {
        const response = await fetch('/assets/achievements.json');
        if (!response.ok) throw new Error('Failed to fetch achievement data');
        const data = await response.json();

        // Flatten all category arrays into a single array
        const flattened = Object.values(data).flat();
        setAllAchievements(flattened);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchAchievements();
  }, []);

  const isUnlocked = (id) => {
    return isAchievementUnlocked({ id }, achievements);
  };

  // Get individual achievement progress
  const getAchievementProgress = (badge) => {
    return getProgressForBadge(badge, achievements);
  };

  const unlockedCount = allAchievements.filter(badge => isUnlocked(badge.id)).length;
  
  const handlePlaySound = () => {
    const audio = new Audio('/assets/unlock.mp3');
    audio.play().catch(() => {});
  };

  const handleClickBadge = (badge) => {
    if (isUnlocked(badge.id)) {
      handlePlaySound();
      setSelected(badge);
    }
  };

  const downloadImage = (src) => {
    const link = document.createElement('a');
    link.href = src;
    link.download = 'achievement.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredBadges = allAchievements
    .filter(badge => subject === 'all' || badge.category === subject)
    .sort((a, b) => {
      const aUnlocked = isUnlocked(a.id);
      const bUnlocked = isUnlocked(b.id);
      if (aUnlocked === bUnlocked) return 0;
      return aUnlocked ? -1 : 1;
    });

  if (loading) {
    return (
      <div className={styles.achievementsCard}>
        <p>Loading achievements...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={styles.achievementsCard}>
        <p>Error: {error}</p>
      </div>
    );
  }

  return (
    <div 
      className={styles.achievementsCard}
      style={{
        '--theme-primary': themeColors.primary,
        '--theme-secondary': themeColors.secondary,
        '--theme-gradient': themeColors.gradient,
        '--theme-progress-gradient': themeColors.progressGradient
      }}
    >
      {/* Subject-branded header band */}
      <div 
        className={styles.headerBrand}
        style={{ 
          background: themeColors.gradient
        }}
      >
        <div className={styles.brandContent}>
          <Trophy className={styles.brandIcon} />
          <h1 className={styles.brandTitle}>{themeColors.name} Achievements</h1>
        </div>
      </div>

      <div className={styles.progressHeader}>
        <h2>Your Progress</h2>
        <div className={styles.progressSection}>
          <span className={styles.progressText}>
            {unlockedCount} of {allAchievements.length} earned
          </span>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill} 
              style={{ 
                width: `${Math.round((unlockedCount / allAchievements.length) * 100)}%`,
                background: themeColors.progressGradient
              }} 
            />
          </div>
        </div>
      </div>

      <div className={styles.badgeGrid}>
        {filteredBadges.map((badge) => {
          const progress = getAchievementProgress(badge);
          const isAchievementUnlocked = isUnlocked(badge.id);
          
          return (
            <div
              key={badge.id}
              className={`${styles.badge} ${isAchievementUnlocked ? styles.unlocked : styles.locked}`}
              onClick={() => handleClickBadge(badge)}
            >
              <div className={styles.badgeImageContainer}>
                <img
                  src={isAchievementUnlocked ? badge.image : '/assets/Eduos_Generic.png'}
                  alt={badge.name}
                  className={styles.badgeImage}
                  onError={(e) => {
                    e.target.src = '/assets/Eduos_Generic.png';
                  }}
                  onDoubleClick={() => isAchievementUnlocked && setEnlargedImage(badge.image)}
                />
              </div>
              
              <div className={styles.badgeInfo}>
                <span className={styles.badgeName}>{badge.name}</span>
                
                <div className={styles.individualProgressContainer}>
                  <div className={styles.individualProgressBar}>
                    <div 
                      className={`${styles.individualProgressFill} ${isAchievementUnlocked ? styles.completed : ''}`}
                      style={{ 
                        width: `${progress}%`,
                        background: isAchievementUnlocked ? themeColors.progressGradient : '#64748b'
                      }}
                    />
                  </div>
                  <span className={styles.progressPercentage}>
                    {isAchievementUnlocked ? '100%' : `${Math.round(progress)}%`}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {unlockedCount === 0 && (
        <div className={styles.encouragementText}>
          <p>Keep learning and exploring to unlock your first badge. Try completing a quiz or maintaining a study streak!</p>
        </div>
      )}

      {selected && (
        <div className={styles.modal} onClick={() => setSelected(null)}>
          <div 
            className={styles.modalContent} 
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selected.image}
              alt={selected.name}
              className={styles.modalImage}
              onError={(e) => {
                e.target.src = '/assets/Eduos_Generic.png';
              }}
            />
            <h3>{selected.name}</h3>
            <p>{selected.description}</p>
            <div className={styles.modalButtons}>
              <button 
                className={styles.modalButton}
                onClick={() => window.print()}
                style={{
                  backgroundColor: themeColors.primary
                }}
              >
                Print Certificate
              </button>
              <button 
                className={styles.modalButton}
                onClick={() => downloadImage(selected.image)}
                style={{
                  backgroundColor: themeColors.secondary
                }}
              >
                Download Badge
              </button>
            </div>
          </div>
        </div>
      )}

      {enlargedImage && (
        <div className={styles.modal} onClick={() => setEnlargedImage(null)}>
          <div 
            className={styles.modalContent} 
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={enlargedImage}
              alt="Enlarged Badge"
              style={{ 
                width: '100%', 
                height: 'auto', 
                borderRadius: '12px'
              }}
              onError={(e) => {
                e.target.src = '/assets/Eduos_Generic.png';
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AchievementsCard;